/**
 * DynamicSidebar — iter-438k Phase 6 (Unified Adaptive Dashboard).
 * ═══════════════════════════════════════════════════════════════════
 * Data-driven sidebar that fetches its own nav tree from
 * `GET /api/identity/workspace/{workspaceKey}/nav`.
 *
 * Nothing is hardcoded on the client — adding a permission live
 * updates what a user can see without a redeploy.
 *
 * Props
 * ─────
 *   workspaceKey   the workspace slug (e.g. "events", "school")
 *   collapsible    (default true) — allow user to toggle sections
 *   activeTab      currently active tab_id (highlights matching item)
 *   onNavigate     (item) => void — called on click. If omitted or if
 *                  the click has no tab_id, DynamicSidebar navigates
 *                  by route via React Router.
 *   variant        'light' (default) or 'dark' (admin portal chrome)
 *   collapsed      when true renders icon-only rail
 *   onToggleCollapse   () => void — toggle handler for collapse button
 *   renderHeader   (opts) => ReactNode — replaces workspace pill header
 *   renderFooter   (opts) => ReactNode — appended below nav list
 *   badgeValues    { [badgeKey]: number } — dynamic counter badges;
 *                  items with matching `badge_key` render this value.
 */
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import * as LucideIcons from 'lucide-react';

import { getToken } from '@/lib/authToken';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import usePlatformResolve from '@/hooks/usePlatformResolve';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ScrollArea } from '@/components/ui/scroll-area';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const Fallback = LucideIcons.Circle;

const VARIANTS = {
  light: {
    aside: 'bg-white border-r border-slate-200 text-slate-900',
    sectionTitle: 'text-slate-500 hover:text-slate-700',
    itemBase: 'text-slate-600 hover:bg-slate-50 border-l-2 border-transparent',
    itemActive: 'bg-slate-100 text-slate-900 font-medium border-l-2',
    errorBox: 'rounded-md bg-red-50 border border-red-200 p-3',
    emptyBox: 'rounded-md bg-slate-50 border border-slate-200 p-3 text-sm text-slate-600 mt-4',
  },
  dark: {
    aside: 'bg-slate-900 border-r border-slate-800 text-white',
    sectionTitle: 'text-slate-500 hover:text-slate-300',
    itemBase: 'text-slate-400 hover:text-white hover:bg-slate-800 border-l-2 border-transparent',
    itemActive: 'bg-indigo-600 text-white font-medium border-l-2 border-indigo-400',
    errorBox: 'rounded-md bg-red-950/40 border border-red-800 p-3',
    emptyBox: 'rounded-md bg-slate-800 border border-slate-700 p-3 text-sm text-slate-300 mt-4',
  },
};

export const DynamicSidebar = ({
  workspaceKey,
  collapsible = true,
  activeTab,
  onNavigate,
  variant = 'light',
  collapsed = false,
  onToggleCollapse,
  renderHeader,
  renderFooter,
  badgeValues = {},
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsedSections, setCollapsedSections] = useState({});
  const [search, setSearch] = useState('');

  const theme = VARIANTS[variant] || VARIANTS.light;

  // iter 450d — Platform Resolve access gate. When the current user isn't a
  // super-admin AND platform-resolve doesn't list this workspace in their
  // active memberships, we short-circuit the nav fetch and show a friendly
  // "no access" state. Prevents leaking menu items the Person record has
  // no membership for.
  const platformResolve = usePlatformResolve();
  const isBlockedByPlatformResolve = (
    !!platformResolve.data
    && !platformResolve.isSuperAdmin
    && !platformResolve.hasWorkspace(workspaceKey)
  );

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['workspace-nav', workspaceKey],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/identity/workspace/${workspaceKey}/nav`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      return res.json();
    },
    staleTime: 30_000,
    enabled: !!workspaceKey && !isBlockedByPlatformResolve,
    retry: 1,
  });

  const workspace = data?.workspace;
  const sections = data?.sections || [];

  const handleClick = (item) => {
    // Tab-driven items dispatch to caller's tab state; pure-route items
    // fall through to React Router. onNavigate wins when provided.
    if (typeof onNavigate === 'function') {
      onNavigate(item);
      if (!item.tab_id && item.route) navigate(item.route);
      return;
    }
    if (item.route) navigate(item.route);
  };

  // ── skeleton / error / empty states ────────────────────────────
  const asideClass = cn(
    // iter-438k · bug#1 fix: aside must be h-full so the inner
    // ScrollArea (flex-1) has a bounded height to scroll against.
    // Without this the whole menu grew past the viewport with no scroll.
    'flex flex-col h-full min-h-0 transition-all duration-300',
    collapsed ? 'w-[68px]' : 'w-64',
    theme.aside,
  );

  if (isBlockedByPlatformResolve) {
    return (
      <aside className={asideClass} data-testid="dynamic-sidebar-no-access">
        <div className="p-4">
          <div className={theme.errorBox || 'p-3 rounded bg-slate-100 text-slate-600 text-sm'}>
            <p className="text-sm font-medium">Workspace not linked</p>
            <p className="text-xs mt-1 opacity-80">
              This workspace isn&apos;t part of your Person record. Ask an admin to
              link a membership if you should have access.
            </p>
          </div>
        </div>
      </aside>
    );
  }

  if (isLoading) {
    return (
      <aside className={asideClass} data-testid="dynamic-sidebar-loading">
        <div className="p-4 space-y-3">
          {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-8" />)}
        </div>
      </aside>
    );
  }

  if (isError) {
    return (
      <aside className={asideClass} data-testid="dynamic-sidebar-error">
        <div className="p-4">
          <div className={theme.errorBox}>
            <p className="text-sm font-medium">Menu unavailable</p>
            <p className="text-xs mt-1 opacity-80">{error?.message || 'Please try again'}</p>
          </div>
        </div>
      </aside>
    );
  }

  // Section-level search filter (client-side only)
  const filteredSections = search
    ? sections
        .map((s) => ({
          ...s,
          items: s.items.filter((it) =>
            (it.label || '').toLowerCase().includes(search.toLowerCase()),
          ),
        }))
        .filter((s) => s.items.length)
    : sections;

  return (
    <TooltipProvider delayDuration={0}>
      <aside
        className={asideClass}
        data-testid={`dynamic-sidebar-${workspaceKey}`}
      >
        {/* Header slot — custom header wins, otherwise default workspace pill */}
        {typeof renderHeader === 'function'
          ? renderHeader({ workspace, collapsed, onToggleCollapse, variant })
          : (
            <DefaultHeader
              workspace={workspace}
              collapsed={collapsed}
              onToggleCollapse={onToggleCollapse}
              variant={variant}
            />
          )}

        {/* Search — hidden when collapsed */}
        {!collapsed && (
          <div className="px-3 py-2">
            <div className="relative">
              <LucideIcons.Search className={cn(
                'absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4',
                variant === 'dark' ? 'text-slate-500' : 'text-slate-400',
              )} />
              <input
                type="text"
                placeholder="Search menu..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={cn(
                  'w-full rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 border-0',
                  variant === 'dark'
                    ? 'bg-slate-800 text-white placeholder-slate-500'
                    : 'bg-slate-100 text-slate-900 placeholder-slate-400',
                )}
                data-testid="dynamic-sidebar-search"
              />
            </div>
          </div>
        )}

        <ScrollArea className="flex-1">
          <nav className="py-2">
            {filteredSections.length === 0 && (
              <div className="px-4">
                <div className={theme.emptyBox}>
                  {search ? 'No menu items match your search.' : 'This workspace has no navigation items configured yet.'}
                </div>
              </div>
            )}

            {filteredSections.map((section) => {
              const isSectionCollapsed = !!collapsedSections[section.title];
              return (
                <div
                  key={section.title}
                  className="mb-3"
                  data-testid={`dynamic-sidebar-section-${section.title.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {!collapsed && (collapsible ? (
                    <button
                      type="button"
                      onClick={() =>
                        setCollapsedSections((c) => ({ ...c, [section.title]: !isSectionCollapsed }))
                      }
                      className={cn(
                        'w-full flex items-center justify-between px-4 py-1.5 text-xs font-semibold uppercase tracking-wide',
                        theme.sectionTitle,
                      )}
                    >
                      <span>{section.title}</span>
                      <LucideIcons.ChevronDown
                        className={cn('h-3.5 w-3.5 transition-transform', isSectionCollapsed && '-rotate-90')}
                      />
                    </button>
                  ) : (
                    <div className={cn(
                      'px-4 py-1.5 text-xs font-semibold uppercase tracking-wide',
                      theme.sectionTitle,
                    )}>
                      {section.title}
                    </div>
                  ))}

                  {(collapsed || !isSectionCollapsed) && (
                    <ul className="mt-1 space-y-0.5">
                      {section.items.map((item) => {
                        const Icon = LucideIcons[item.icon] || Fallback;
                        const routeTarget = (item.route || '').split('?')[0];
                        const isRouteActive = routeTarget && !item.tab_id &&
                          (location.pathname === routeTarget ||
                            location.pathname.startsWith(`${routeTarget}/`));
                        const isTabActive = item.tab_id && activeTab === item.tab_id;
                        const active = isTabActive || isRouteActive;

                        const badgeCount = item.badge_key
                          ? (badgeValues?.[item.badge_key] || 0)
                          : 0;

                        const btn = (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleClick(item)}
                            disabled={!item.route && !item.tab_id}
                            className={cn(
                              'w-full text-sm h-9 rounded-none font-normal',
                              collapsed ? 'justify-center px-0' : 'justify-start px-4',
                              active ? theme.itemActive : theme.itemBase,
                            )}
                            style={active && workspace?.color
                              ? { borderLeftColor: workspace.color }
                              : {}}
                            data-testid={`dynamic-sidebar-item-${item.id}`}
                          >
                            <Icon className={cn(
                              'h-4 w-4 flex-shrink-0',
                              !collapsed && 'mr-3',
                            )} />
                            {!collapsed && (
                              <>
                                <span className="truncate flex-1 text-left">{item.label}</span>
                                {badgeCount > 0 && (
                                  <Badge className="bg-amber-500 text-white text-[10px] px-1.5 py-0 ml-1">
                                    {badgeCount}
                                  </Badge>
                                )}
                                {item.badge && (
                                  <Badge className="bg-emerald-500 text-white text-[10px] px-1.5 py-0 ml-1">
                                    {item.badge}
                                  </Badge>
                                )}
                              </>
                            )}
                          </Button>
                        );

                        if (collapsed) {
                          return (
                            <li key={item.id}>
                              <Tooltip>
                                <TooltipTrigger asChild>{btn}</TooltipTrigger>
                                <TooltipContent side="right" className="flex items-center gap-2">
                                  {item.label}
                                  {item.badge && (
                                    <Badge className="bg-emerald-500">{item.badge}</Badge>
                                  )}
                                </TooltipContent>
                              </Tooltip>
                            </li>
                          );
                        }
                        return <li key={item.id}>{btn}</li>;
                      })}
                    </ul>
                  )}
                </div>
              );
            })}
          </nav>
        </ScrollArea>

        {/* Footer slot — optional */}
        {typeof renderFooter === 'function' &&
          renderFooter({ workspace, collapsed, variant })}
      </aside>
    </TooltipProvider>
  );
};

const DefaultHeader = ({ workspace, collapsed, onToggleCollapse, variant }) => {
  if (!workspace) return null;
  const Icon = LucideIcons[workspace.icon] || Fallback;
  const borderCls = variant === 'dark' ? 'border-slate-800' : 'border-slate-200';
  return (
    <div
      className={cn(
        'px-4 py-4 border-b flex items-center gap-3',
        borderCls,
        collapsed && 'justify-center px-2',
      )}
      data-testid="dynamic-sidebar-header"
    >
      <div
        className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: `${workspace.color || '#3b82f6'}25` }}
      >
        <Icon className="h-4 w-4" style={{ color: workspace.color || '#3b82f6' }} />
      </div>
      {!collapsed && (
        <div className="min-w-0 flex-1">
          <div className={cn(
            'text-sm font-semibold truncate',
            variant === 'dark' ? 'text-white' : 'text-slate-900',
          )}>
            {workspace.label}
          </div>
          {workspace.description && (
            <div className={cn(
              'text-xs truncate',
              variant === 'dark' ? 'text-slate-400' : 'text-slate-500',
            )}>
              {workspace.description}
            </div>
          )}
        </div>
      )}
      {onToggleCollapse && (
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleCollapse}
          className={cn(
            'h-7 w-7 flex-shrink-0',
            variant === 'dark' ? 'text-slate-400 hover:text-white hover:bg-slate-800' : '',
          )}
          data-testid="dynamic-sidebar-toggle-collapse"
        >
          {collapsed
            ? <LucideIcons.ChevronRight className="h-4 w-4" />
            : <LucideIcons.ChevronLeft className="h-4 w-4" />}
        </Button>
      )}
    </div>
  );
};

export default DynamicSidebar;
